/** @format */

import { useTranslation } from "react-i18next";
import { AiFillHeart, AiOutlineShoppingCart } from "react-icons/ai";
import { FaEye, FaGift, FaShopify } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  devise,
  TruncateText,
  useProductTracking,
  WhatsAppButton,
} from "../../utils/utils";
import Ratings from "../Ratings";

// ─── Time ago helper ──────────────────────────────────────────────────────────
const timeAgo = (dateStr, t) => {
  if (!dateStr) return null;
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMins < 1) return t("Just now");
  if (diffMins < 60) return `${diffMins} ${t("min")}`;
  if (diffHours < 24)
    return `${diffHours} ${diffHours === 1 ? t("hour") : t("hours")}`;
  if (diffDays < 7)
    return `${diffDays} ${diffDays === 1 ? t("day") : t("days")}`;
  if (diffWeeks < 5)
    return `${diffWeeks} ${diffWeeks === 1 ? t("week") : t("weeks")}`;
  return `${diffMonths} ${diffMonths === 1 ? t("month") : t("months")}`;
};

// ─── Clock Icon ───────────────────────────────────────────────────────────────
const ClockIcon = () => (
  <svg
    width='10'
    height='10'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <polyline points='12 6 12 12 16 14' />
  </svg>
);

// ─── Chat Icon ────────────────────────────────────────────────────────────────
const ChatIcon = () => (
  <svg
    width='13'
    height='13'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────
const ProductCard = ({
  product,
  customerInfo,
  onAddCard,
  onAddWishlist,
  onSendMessage,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { trackAction } = useProductTracking(product._id);

  const discountedPrice = product.discount
    ? Math.floor((parseFloat(product.price) * (100 - product.discount)) / 100)
    : product.price;

  const savings = product.discount
    ? Math.floor(parseFloat(product.price) * (product.discount / 100))
    : 0;

  const postedAgo = timeAgo(product.createdAt, t);

  const handleNavigate = () => {
    navigate(`/product/details/${product._id}`);
    trackAction("viewDetails");
  };

  return (
    <article
      className='group relative flex flex-col bg-white rounded-2xl overflow-hidden
                 transition-all duration-300 ease-out
                 hover:-translate-y-1 hover:shadow-[0_4px_30px_rgba(0,0,0,0.10)]'
      style={{
        boxShadow: "0 1px 4px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.05)",
        border: "1px solid rgba(0,0,0,0.07)",
      }}
    >
      {/* ─── Image Zone ────────────────────────────────────── */}
      <div className='relative aspect-square overflow-hidden bg-gradient-to-br from-slate-50 to-gray-100'>
        {/* Discount Badge */}
        {product.discount > 0 && (
          <div className='absolute top-3 left-3 z-20'>
            <span
              className='inline-flex items-center justify-center px-2.5 py-0.5
                         text-[11px] font-black text-white rounded-full'
              style={{
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                boxShadow: "0 2px 6px rgba(239,68,68,0.4)",
                letterSpacing: "0.02em",
              }}
            >
              -{product.discount}%
            </span>
            {savings > 0 && (
              <span
                className='text-[10px] font-bold px-2 py-0.5 rounded-full'
                style={{
                  background: "rgba(16,185,129,0.2)",
                  color: "#059669",
                }}
              >
                -{savings} {devise}
              </span>
            )}
          </div>
        )}

        {/* Out of Stock Overlay */}
        {product.stock <= 0 && (
          <div className='absolute inset-0 z-10 flex items-center justify-center bg-white/55 backdrop-blur-[3px]'>
            <span
              className='px-4 py-1.5 text-[11px] font-bold tracking-[0.12em]
                         uppercase text-white rounded-full'
              style={{
                background: "linear-gradient(135deg, #374151 0%, #111827 100%)",
              }}
            >
              {t("Out of Stock")}
            </span>
          </div>
        )}

        {/* Wishlist Button */}
        <button
          onClick={() => {
            onAddWishlist(product);
            trackAction("addToWishlist");
          }}
          className='absolute top-3 right-3 z-20 w-8 h-8
                     flex items-center justify-center rounded-full
                     bg-white/85 backdrop-blur-sm shadow-sm
                     text-slate-300 hover:text-red-500 hover:bg-white
                     transition-all duration-200 hover:scale-110 active:scale-95'
          title={t("Add to wishlist")}
          style={{ border: "1px solid rgba(0,0,0,0.07)" }}
        >
          <AiFillHeart size={14} />
        </button>

        {/* Product Image */}
        <div onClick={handleNavigate} className='cursor-pointer w-full h-full'>
          <img
            className='w-full h-full object-contain p-5
                       transition-transform duration-500 ease-out
                       group-hover:scale-105'
            src={product.images[0]?.url || product.images[0]}
            alt={product.name}
            loading='lazy'
          />
        </div>

        {/* Hover Quick Actions */}
        <div
          className='absolute bottom-0 left-0 right-0 z-20
                     flex justify-center gap-2 pb-3 pt-10
                     opacity-0 translate-y-2
                     group-hover:opacity-100 group-hover:translate-y-0
                     transition-all duration-300 ease-out md-lg:opacity-100'
          style={{
            background:
              "linear-gradient(to top, rgba(255,255,255,0.97) 0%, transparent 100%)",
          }}
        >
          <button
            onClick={handleNavigate}
            className='flex items-center gap-1.5 px-3 py-1.5
                       text-[11px] font-semibold text-slate-600 bg-white
                       rounded-lg shadow-sm
                       hover:bg-slate-800 hover:text-white
                       transition-all duration-200 active:scale-95'
            style={{ border: "1px solid rgba(0,0,0,0.09)" }}
          >
            <FaEye size={12} />
            <span>{t("View")}</span>
          </button>

          <button
            onClick={() => {
              onAddCard(product._id);
              trackAction("addToCart");
            }}
            disabled={product.stock <= 0}
            className='flex items-center gap-1.5 px-3 py-1.5
                       text-[11px] font-semibold text-white
                       rounded-lg shadow-sm
                       transition-all duration-200 active:scale-95
                       disabled:opacity-50 disabled:cursor-not-allowed'
            style={{
              background:
                product.stock > 0
                  ? "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)"
                  : "#9ca3af",
              boxShadow:
                product.stock > 0 ? "0 2px 8px rgba(37,99,235,0.3)" : "none",
            }}
          >
            <AiOutlineShoppingCart size={13} />
            <span>{t("Cart")}</span>
          </button>
        </div>
      </div>

      {/* ─── Card Body ──────────────────────────────────────── */}
      <div className='flex flex-col flex-1 px-4 pt-3 pb-4 gap-2.5'>
        {/* Shop + Time Row */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-1.5 min-w-0'>
            <FaShopify size={10} className='text-slate-400 flex-shrink-0' />
            <span
              className='text-[10px] font-semibold uppercase tracking-widest
                         text-slate-400 truncate'
            >
              {product.shopName}
            </span>
          </div>

          {/* Time since listing */}
          {postedAgo && (
            <div
              className='flex items-center gap-1 flex-shrink-0 ml-2
                         text-[10px] font-medium text-slate-400'
            >
              <ClockIcon />
              <span>{postedAgo}</span>
            </div>
          )}
        </div>

        {/* Product Name */}
        <div
          onClick={handleNavigate}
          className='cursor-pointer text-sm text-slate-800 leading-snug
                       hover:text-blue-600 transition-colors duration-150'
        >
          <TruncateText className='' text={product.name} maxLength={40} />
        </div>

        {/* Rating + Popularity */}
        {(product?.rating > 0 || product?.popularity?.score > 0) && (
          <div className='flex items-center gap-2'>
            {product?.rating > 0 && <Ratings ratings={product.rating} />}
            {product?.popularity?.score > 0 && (
              <span
                className='text-sm font-light px-2 py-0.5 rounded-full'
                style={{
                  background: "rgba(234,179,8,0.1)",
                  color: "#b45309",
                }}
              >
                {product?.popularity?.score} pts
              </span>
            )}
          </div>
        )}

        {/* Price Block */}
        <div className='gap-1'>
          <div
            className='flex items-center justify-between rounded-xl px-2 py-2 mt-0.5'
            style={{
              background: "linear-gradient(135deg, #f0f9ff 0%, #e8f4fd 100%)",
              border: "1px solid rgba(14,165,233,0.13)",
            }}
          >
            {/* Main price */}
            <span className='text-sm font-black text-slate-900 tracking-tight gap-2'>
              {discountedPrice}
              <span className='text-xs font-normal text-slate-500 ml-1'>
                {devise}
              </span>
            </span>

            {/* Original + savings */}
            <div className='flex items-center gap-1.5'>
              {product.discount > 0 && (
                <span className='text-[10px] text-slate-400 line-through font-medium'>
                  {product.price} {devise}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Badges */}
        <div className='flex flex-wrap items-center gap-1.5'>
          {product.stock > 0 ? (
            <span
              className='inline-flex items-center gap-1 px-2.5 py-[3px]
                         text-[10px] font-semibold rounded-full'
              style={{
                background: "rgba(16,185,129,0.08)",
                color: "#059669",
                border: "1px solid rgba(16,185,129,0.18)",
              }}
            >
              <span className='w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse' />
              {t("In Stock")} &middot; {product.stock}
            </span>
          ) : (
            <span
              className='inline-flex items-center gap-1 px-2.5 py-[3px]
                         text-[10px] font-semibold rounded-full'
              style={{
                background: "rgba(239,68,68,0.07)",
                color: "#dc2626",
                border: "1px solid rgba(239,68,68,0.13)",
              }}
            >
              <span className='w-1.5 h-1.5 rounded-full bg-red-400' />
              {t("Out of Stock")}
            </span>
          )}

          {product.deliveryType === "free" && (
            <span
              className='inline-flex items-center gap-1 px-2.5 py-[3px]
                         text-[10px] font-semibold rounded-full'
              style={{
                background: "rgba(99,102,241,0.07)",
                color: "#4f46e5",
                border: "1px solid rgba(99,102,241,0.14)",
              }}
            >
              <FaGift size={8} />
              {t("Free delivery")}
            </span>
          )}
        </div>

        {/* Divider */}
        <div
          className='w-full my-0.5'
          style={{ height: "1px", background: "rgba(0,0,0,0.05)" }}
        />

        {/* CTA Buttons */}
        <div className='flex gap-2'>
          {/* Chat Button */}
          <button
            onClick={() => {
              onSendMessage(product);
              trackAction("sendMessage");
            }}
            className='flex-1 flex items-center justify-center gap-1.5
                       py-2 text-[11px] font-bold text-white rounded-xl
                       transition-all duration-200 active:scale-95
                       hover:brightness-110'
            style={{
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              boxShadow: "0 2px 8px rgba(59,130,246,0.28)",
            }}
          >
            <ChatIcon />
            {t("Chat")}
          </button>

          {/* WhatsApp Button */}
          <div
            className='flex-1 flex items-center justify-center
                       rounded-xl overflow-hidden
                       transition-all duration-200 active:scale-95
                       text-[11px] font-bold text-white
                       hover:brightness-110'
            style={{
              background: "linear-gradient(135deg, #22c55e 0%, #15a34a 100%)",
              boxShadow: "0 2px 8px rgba(34,197,94,0.28)",
            }}
          >
            <WhatsAppButton
              buttonName='WhatsApp'
              sellerPhone={product.sellerId?.shopInfo?.telephone}
              message={{ messageType: "product", productInfo: product }}
              productId={product._id}
              onClickCallback={() => trackAction("whatsapp")}
              width={"full"}
            />
          </div>
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
